import type { BillingPlan, BillingLimitValue, BillingUsage } from '@store/api/billing/billingApi';
import { bytesFromGigabytes } from './formatBytes';

export type ProEligibilityBlocker = {
  key:
    | 'projects'
    | 'orgMembers'
    | 'projectMembers'
    | 'channelsPerProject'
    | 'monthlyUploads'
    | 'storage'
    | 'unknown';
  current: number;
  limit: number;
  label?: string;
};

const numericLimit = (value?: BillingLimitValue) =>
  typeof value === 'number' ? value : null;

export const getProEligibilityBlockers = (
  usage?: BillingUsage,
  proPlan?: BillingPlan
): ProEligibilityBlocker[] => {
  const blockers: ProEligibilityBlocker[] = [];
  const projectCount = usage?.maxProjects?.current;
  const orgMemberCount = usage?.maxOrgMembers?.current;
  const projectMemberRows = usage?.maxProjectMembers?.byProject;
  const channelRows = usage?.maxChannelsPerProject?.byProject;
  const monthlyUploads = usage?.monthlyBundleUploads?.current;
  const storageBytes = usage?.maxStorageUsed?.currentBytes;
  const projectLimit = numericLimit(proPlan?.limits?.maxProjects);
  const orgMemberLimit = numericLimit(proPlan?.limits?.maxOrgMembers);
  const projectMemberLimit = numericLimit(proPlan?.limits?.maxProjectMembers);
  const channelLimit = numericLimit(proPlan?.limits?.maxChannelsPerProject);
  const monthlyUploadLimit = numericLimit(proPlan?.limits?.monthlyBundleUploads);
  const storageLimitGb = numericLimit(proPlan?.limits?.maxStorageUsedGB);
  const storageLimitBytes =
    typeof storageLimitGb === 'number' ? bytesFromGigabytes(storageLimitGb) : null;

  if (!usage || !proPlan) {
    return [{ key: 'unknown', current: 1, limit: 0 }];
  }

  if (typeof projectCount !== 'number' || typeof projectLimit !== 'number') {
    blockers.push({ key: 'unknown', current: 1, limit: 0 });
  }
  if (typeof projectCount === 'number' && typeof projectLimit === 'number' && projectCount > projectLimit) {
    blockers.push({ key: 'projects', current: projectCount, limit: projectLimit });
  }
  if (typeof orgMemberCount !== 'number' || typeof orgMemberLimit !== 'number') {
    blockers.push({ key: 'unknown', current: 1, limit: 0 });
  }
  if (
    typeof orgMemberCount === 'number' &&
    typeof orgMemberLimit === 'number' &&
    orgMemberCount > orgMemberLimit
  ) {
    blockers.push({
      key: 'orgMembers',
      current: orgMemberCount,
      limit: orgMemberLimit,
    });
  }
  if (!Array.isArray(projectMemberRows) || typeof projectMemberLimit !== 'number') {
    blockers.push({ key: 'unknown', current: 1, limit: 0 });
  } else {
    for (const row of projectMemberRows) {
      const current = row.current ?? row.active;
      if (typeof current === 'number' && current > projectMemberLimit) {
        blockers.push({
          key: 'projectMembers',
          current,
          limit: projectMemberLimit,
          label: row.projectName || row.projectSlug || row.projectId,
        });
      }
    }
  }
  if (!Array.isArray(channelRows) || typeof channelLimit !== 'number') {
    blockers.push({ key: 'unknown', current: 1, limit: 0 });
  } else {
    for (const row of channelRows) {
      if (typeof row.current === 'number' && row.current > channelLimit) {
        blockers.push({
          key: 'channelsPerProject',
          current: row.current,
          limit: channelLimit,
          label: row.projectName || row.projectSlug || row.projectId,
        });
      }
    }
  }
  if (typeof monthlyUploads !== 'number' || typeof monthlyUploadLimit !== 'number') {
    blockers.push({ key: 'unknown', current: 1, limit: 0 });
  } else if (monthlyUploads > monthlyUploadLimit) {
    blockers.push({
      key: 'monthlyUploads',
      current: monthlyUploads,
      limit: monthlyUploadLimit,
    });
  }
  if (typeof storageBytes !== 'number' || typeof storageLimitBytes !== 'number') {
    blockers.push({ key: 'unknown', current: 1, limit: 0 });
  }
  if (
    typeof storageBytes === 'number' &&
    typeof storageLimitBytes === 'number' &&
    storageBytes > storageLimitBytes
  ) {
    blockers.push({ key: 'storage', current: storageBytes, limit: storageLimitBytes });
  }

  return blockers;
};

export const fitsProLimits = (usage?: BillingUsage, proPlan?: BillingPlan) =>
  Boolean(proPlan) && getProEligibilityBlockers(usage, proPlan).length === 0;
